import React, { useState, useEffect, useRef } from 'react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import { 
  ArrowUpRight, 
  ArrowDownLeft, 
  MoreHorizontal, 
  Zap, 
  Wallet,
  Globe,
  Plus,
  Send,
  Download,
  Bot
} from './Icons';
import { Transaction, Currency, ChartData, Language } from '../types';
import { sendMessageToGemini } from '../services/geminiService';
import { translations } from '../utils/translations';

// --- MOCK DATA ---
const mockTransactions: Transaction[] = [
  { id: 't1', merchantName: 'Shopify Payout', amount: 2350.00, currency: Currency.USD, date: '2023-10-24', status: 'completed', type: 'incoming', category: 'Sales' },
  { id: 't2', merchantName: 'AWS Services', amount: 124.50, currency: Currency.USD, date: '2023-10-23', status: 'completed', type: 'outgoing', category: 'Infrastructure' },
  { id: 't3', merchantName: 'Supplier HK Ltd', amount: 4500.00, currency: Currency.USD, date: '2023-10-22', status: 'pending', type: 'outgoing', category: 'Inventory' },
  { id: 't4', merchantName: 'Stripe Earnings', amount: 1890.20, currency: Currency.EUR, date: '2023-10-21', status: 'completed', type: 'incoming', category: 'Sales' },
  { id: 't5', merchantName: 'Fiverr Freelance', amount: 350.00, currency: Currency.USD, date: '2023-10-20', status: 'completed', type: 'outgoing', category: 'Services' },
];

const chartData: ChartData[] = [
  { name: 'Mon', income: 4000, expenses: 2400 },
  { name: 'Tue', income: 3000, expenses: 1398 },
  { name: 'Wed', income: 2000, expenses: 9800 },
  { name: 'Thu', income: 2780, expenses: 3908 },
  { name: 'Fri', income: 1890, expenses: 4800 },
  { name: 'Sat', income: 2390, expenses: 3800 },
  { name: 'Sun', income: 3490, expenses: 4300 },
];

// --- COMPONENTS ---

interface ViewProps {
    language: Language;
}

export const DashboardHome: React.FC<ViewProps> = ({ language }) => {
  const t = translations[language].dashboard.home;
  
  return (
    <div className="space-y-6">
      {/* Top Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t.greeting}, Alex</h1>
          <p className="text-slate-500">{t.subtitle}</p>
        </div>
        <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-slate-700 font-medium rounded-lg hover:bg-gray-50 transition-colors">
                <Download className="w-4 h-4" /> {t.statement}
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-brand-green text-brand-dark font-bold rounded-lg hover:bg-green-400 transition-colors shadow-lg shadow-green-200/50">
                <Plus className="w-4 h-4" /> {t.addMoney}
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-brand-blue text-white font-bold rounded-lg hover:bg-brand-dark transition-colors shadow-lg shadow-blue-200/50">
                <Send className="w-4 h-4" /> {t.sendFunds}
            </button>
        </div>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 relative overflow-hidden group">
            <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Wallet className="w-24 h-24 text-brand-blue" />
            </div>
            <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700">🇺🇸</div>
                <span className="font-semibold text-slate-600">US Dollar</span>
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-1">$42,500.20</h2>
            <div className="flex items-center text-sm text-green-600 font-medium">
                <ArrowUpRight className="w-4 h-4 mr-1" /> +12.5% {t.thisWeek}
            </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 relative overflow-hidden group">
             <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Globe className="w-24 h-24 text-brand-purple" />
            </div>
            <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-xs font-bold text-purple-700">🇪🇺</div>
                <span className="font-semibold text-slate-600">Euro</span>
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-1">€18,240.00</h2>
            <div className="flex items-center text-sm text-slate-400 font-medium">
                {t.stable}
            </div>
        </div>

        <div className="bg-gradient-to-br from-brand-lilac to-purple-200 p-6 rounded-2xl shadow-sm border border-purple-200 relative overflow-hidden flex flex-col justify-center items-center text-center cursor-pointer hover:shadow-md transition-all">
            <div className="w-12 h-12 bg-white/50 rounded-full flex items-center justify-center mb-3">
                <Plus className="w-6 h-6 text-brand-purple" />
            </div>
            <h3 className="font-bold text-brand-purple text-lg">{t.openBalance}</h3>
            <p className="text-brand-purple/70 text-sm">{t.moreCurrencies}</p>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart Section */}
        <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-lg text-slate-900">{t.cashFlow}</h3>
                <select className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-1 text-sm text-slate-600 outline-none focus:ring-2 focus:ring-brand-blue/20">
                    <option>Last 7 days</option>
                    <option>Last 30 days</option>
                </select>
            </div>
            <div className="h-[300px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                            <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#2ED06E" stopOpacity={0.2}/>
                                <stop offset="95%" stopColor="#2ED06E" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#005EA6" stopOpacity={0.2}/>
                                <stop offset="95%" stopColor="#005EA6" stopOpacity={0}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#64748B', fontSize: 12}} dy={10} />
                        <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748B', fontSize: 12}} />
                        <Tooltip 
                            contentStyle={{ backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                            itemStyle={{ fontSize: '12px', fontWeight: 600 }}
                        />
                        <Area type="monotone" dataKey="income" stroke="#2ED06E" strokeWidth={3} fillOpacity={1} fill="url(#colorIncome)" />
                        <Area type="monotone" dataKey="expenses" stroke="#005EA6" strokeWidth={3} fillOpacity={1} fill="url(#colorExpenses)" />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>

        {/* Quick Activity */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <div className="flex justify-between items-center mb-6">
                <h3 className="font-bold text-lg text-slate-900">{t.recentActivity}</h3>
                <button className="text-brand-blue text-sm font-medium hover:underline">{t.viewAll}</button>
            </div>
            <div className="space-y-4">
                {mockTransactions.slice(0, 4).map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between group cursor-pointer">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                tx.type === 'incoming' ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-600'
                            }`}>
                                {tx.type === 'incoming' ? <ArrowDownLeft className="w-5 h-5" /> : <ArrowUpRight className="w-5 h-5" />}
                            </div>
                            <div>
                                <p className="text-sm font-bold text-slate-900 group-hover:text-brand-blue transition-colors">{tx.merchantName}</p>
                                <p className="text-xs text-slate-500">{tx.category}</p>
                            </div>
                        </div>
                        <div className="text-right">
                             <p className={`text-sm font-bold ${
                                tx.type === 'incoming' ? 'text-brand-green' : 'text-slate-900'
                            }`}>
                                {tx.type === 'incoming' ? '+' : '-'}{tx.currency === Currency.USD ? '$' : '€'}{tx.amount.toFixed(2)}
                            </p>
                            <p className="text-[10px] text-slate-400">{tx.date}</p>
                        </div>
                    </div>
                ))}
            </div>
            <button className="w-full mt-6 py-3 border border-gray-200 rounded-xl text-sm font-bold text-slate-600 hover:bg-gray-50 transition-colors">
                {t.viewReport}
            </button>
        </div>
      </div>
    </div>
  );
};

export const TransactionsView: React.FC<ViewProps> = ({ language }) => {
    const t = translations[language].dashboard.transactions;
    return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
             <h2 className="text-xl font-bold text-slate-900">{t.title}</h2>
             <div className="flex gap-2">
                <button className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50 rounded-lg border border-gray-200">{t.filter}</button>
                <button className="px-3 py-1.5 text-sm font-medium text-white bg-brand-blue rounded-lg">{t.export}</button>
             </div>
        </div>
        <table className="w-full text-left text-sm text-slate-600">
            <thead className="bg-slate-50 text-slate-500 font-medium">
                <tr>
                    <th className="px-6 py-4">{t.cols.merchant}</th>
                    <th className="px-6 py-4">{t.cols.date}</th>
                    <th className="px-6 py-4">{t.cols.status}</th>
                    <th className="px-6 py-4 text-right">{t.cols.amount}</th>
                    <th className="px-6 py-4"></th>
                </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
                {mockTransactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                    tx.type === 'incoming' ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-600'
                                }`}>
                                    {tx.category.charAt(0)}
                                </div>
                                <div>
                                    <div className="font-bold text-slate-900">{tx.merchantName}</div>
                                    <div className="text-xs text-slate-400">{tx.category}</div>
                                </div>
                            </div>
                        </td>
                        <td className="px-6 py-4">{tx.date}</td>
                        <td className="px-6 py-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                tx.status === 'completed' ? 'bg-green-100 text-green-800' : 
                                tx.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                            }`}>
                                {tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
                            </span>
                        </td>
                        <td className={`px-6 py-4 text-right font-bold ${
                            tx.type === 'incoming' ? 'text-brand-green' : 'text-slate-900'
                        }`}>
                             {tx.type === 'incoming' ? '+' : '-'}{tx.currency === Currency.USD ? '$' : '€'}{tx.amount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 text-right">
                            <button className="text-slate-400 hover:text-brand-blue">
                                <MoreHorizontal className="w-5 h-5" />
                            </button>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
    );
};

export const AIAssistantView: React.FC = () => {
    const [messages, setMessages] = useState<{role: 'user' | 'model', text: string}[]>([
        {role: 'model', text: 'Hello! I am BancoBot. How can I assist you with your finances today?'}
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const endRef = useRef<HTMLDivElement>(null);

    const handleSend = async () => {
        if (!input.trim() || loading) return;
        
        const userMsg = input;
        setInput('');
        setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
        setLoading(true);

        const response = await sendMessageToGemini(userMsg);
        
        setMessages(prev => [...prev, { role: 'model', text: response }]);
        setLoading(false);
    };

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    return (
        <div className="bg-white rounded-2xl shadow-xl border border-brand-lilac/50 h-[calc(100vh-8rem)] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-brand-dark to-brand-blue p-6 text-white flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur rounded-full flex items-center justify-center border border-white/30">
                        <Bot className="w-7 h-7 text-brand-lime" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold">BancoBot AI</h2>
                        <p className="text-sm text-blue-100">Your personal financial analyst</p>
                    </div>
                </div>
                <div className="px-3 py-1 rounded-full bg-brand-green text-brand-dark text-xs font-bold uppercase">
                    Online
                </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50">
                {messages.map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-2xl px-5 py-4 shadow-sm ${
                            msg.role === 'user' 
                            ? 'bg-brand-blue text-white rounded-br-none' 
                            : 'bg-white text-slate-700 border border-gray-200 rounded-bl-none'
                        }`}>
                            <p className="leading-relaxed whitespace-pre-wrap">{msg.text}</p>
                        </div>
                    </div>
                ))}
                {loading && (
                    <div className="flex justify-start">
                        <div className="bg-white px-5 py-4 rounded-2xl rounded-bl-none border border-gray-200 shadow-sm flex gap-2">
                             <div className="w-2 h-2 bg-brand-blue rounded-full animate-bounce"></div>
                             <div className="w-2 h-2 bg-brand-blue rounded-full animate-bounce delay-100"></div>
                             <div className="w-2 h-2 bg-brand-blue rounded-full animate-bounce delay-200"></div>
                        </div>
                    </div>
                )}
                <div ref={endRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-gray-200">
                <div className="flex gap-2">
                    <input 
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                        placeholder="Ask about spending trends, invoicing, or exchange rates..."
                        className="flex-1 bg-gray-50 border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all"
                    />
                    <button 
                        onClick={handleSend}
                        disabled={loading}
                        className={`px-6 rounded-xl font-bold flex items-center justify-center transition-all ${
                            loading 
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-brand-blue hover:bg-brand-dark text-white shadow-lg hover:shadow-blue-200/50'
                        }`}
                    >
                        <Send className="w-5 h-5" />
                    </button>
                </div>
                <div className="text-center mt-2">
                    <p className="text-[10px] text-slate-400">AI can make mistakes. Please verify financial details.</p>
                </div>
            </div>
        </div>
    );
};